import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock, FaSpinner, FaClock, FaWhatsapp, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isPendingApproval, setIsPendingApproval] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsPendingApproval(false);
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      // Check if error is about pending approval
      if (result.error && result.error.toLowerCase().includes('pending approval')) {
        setIsPendingApproval(true);
      } else {
        setError(result.error);
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-3 sm:p-4" 
         style={{ background: 'linear-gradient(135deg, #0A1929 0%, #1E3A5F 100%)' }}>
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-5xl font-bold mb-1 sm:mb-2" style={{ color: '#FFD700' }}>
            Arth Gurukul
          </h1>
          <p className="text-[#FFD700] text-base sm:text-lg font-semibold">Your School for Lifelong Financial Wellness</p>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">Navigate. Plan. Prosper.</p>
        </div>

        {/* Login Card */}
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl shadow-2xl p-5 sm:p-8 border border-[#2D5A8F]">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center">Sign In</h2>
          
          {/* Book Demo Button */}
          <a
            href="https://wa.link/c15uzz"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mb-4 sm:mb-6 py-2.5 sm:py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg hover:from-green-600 hover:to-green-700 focus:outline-none transition-all transform hover:scale-[1.02] flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
            data-testid="book-demo-btn"
          >
            <FaWhatsapp size={18} />
            <span>Book Your Free Demo Now</span>
          </a>
          
          {/* Pending Approval Message */}
          {isPendingApproval && (
            <div className="mb-4 p-3 sm:p-4 bg-cyan-500/20 border border-cyan-500 rounded-lg" data-testid="pending-approval-message">
              <div className="flex items-center mb-2">
                <FaClock className="text-cyan-400 mr-2" />
                <span className="text-cyan-400 font-medium text-sm sm:text-base">Account Pending Approval</span>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm">
                Your account is waiting for administrator approval. You will be able to log in once your account has been approved.
              </p>
            </div>
          )}
          
          {/* Regular Error Message */}
          {error && !isPendingApproval && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                  placeholder="advisor@example.com"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="text-gray-400" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] focus:ring-2 focus:ring-[#FFD700]/20 transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#1E3A5F] transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-gray-400 text-sm">
              <Link to="/forgot-password" className="text-[#FFD700] hover:text-[#D4AF37] font-medium transition-colors">
                Forgot Password?
              </Link>
            </p>
            <p className="text-gray-400 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#FFD700] hover:text-[#D4AF37] font-medium transition-colors">
                Register here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center space-y-3">
          {/* Contact Information */}
          <div className="bg-[#1A2942]/50 rounded-lg p-4 border border-[#2D5A8F]">
            <p className="text-gray-300 text-sm font-medium mb-2">Need Help? Contact Us</p>
            <div className="flex items-center justify-center text-sm">
              <a 
                href="mailto:care@mdrtschool.com" 
                className="flex items-center space-x-2 text-[#FFD700] hover:text-[#D4AF37] transition-colors"
                data-testid="contact-email"
              >
                <FaEnvelope size={14} />
                <span>Tech &amp; Training Support only: care@mdrtschool.com</span>
              </a>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="text-gray-400 text-xs">
            <p>© 2026 Arth Gurukul | Tech, Tools &amp; Training Powered by MDRT School | By Financial Nirvana. All rights reserved.</p>
            <p className="mt-1 text-[#FFD700]">Your School for Lifelong Financial Wellness</p>
          </div>
          
          {/* Disclaimer */}
          <div className="mt-4 text-gray-500 text-[10px] leading-relaxed max-w-md mx-auto">
            <p><strong className="text-gray-400">Disclaimer:</strong> MDRT School and Financial Nirvana do not provide any financial, investment, legal, or tax advisory services. We are a capability development and education platform offering tools, technology, and training exclusively for financial services sales professionals. Any decisions taken based on the use of our content, tools, or programmes are solely the responsibility of the user.</p>
          </div>
        </div>
      </div>
      
      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp 
        phoneLink="https://wa.link/c15uzz"
        message="Need help? Chat with us!"
      />
    </div>
  );
};

export default Login;